import { useQuery } from '@tanstack/react-query';
import {
  withholdingClassesQueryKey,
  withholdingKeysQueryKey,
  withholdingOperationCentersQueryKey,
} from './keys';
import type {
  WithholdingClassOption,
  WithholdingKeyOption,
  WithholdingOperationCenterOption,
  WithholdingsCatalogs,
} from '../types';

const STALE_TIME = 5 * 60 * 1000; // los catálogos cambian poco (§7.4)

export interface UseWithholdingClassesParams {
  catalogs?: Pick<WithholdingsCatalogs, 'loadClasses'>;
  enabled?: boolean;
}

/** Carga las clases de retención para el `Select` del modal Agregar. */
export function useWithholdingClasses({ catalogs, enabled = true }: UseWithholdingClassesParams) {
  return useQuery<WithholdingClassOption[]>({
    queryKey: withholdingClassesQueryKey(),
    enabled: enabled && Boolean(catalogs?.loadClasses),
    staleTime: STALE_TIME,
    queryFn: () => catalogs!.loadClasses(),
  });
}

export interface UseWithholdingKeysParams {
  catalogs?: Pick<WithholdingsCatalogs, 'loadKeys'>;
  /** Clase seleccionada; sin ella la query queda deshabilitada. */
  classId: string | null;
  enabled?: boolean;
}

/** Carga las claves de la clase seleccionada (filtradas por clase). */
export function useWithholdingKeys({ catalogs, classId, enabled = true }: UseWithholdingKeysParams) {
  return useQuery<WithholdingKeyOption[]>({
    queryKey: withholdingKeysQueryKey(classId ?? ''),
    enabled: enabled && Boolean(catalogs?.loadKeys) && Boolean(classId),
    staleTime: STALE_TIME,
    queryFn: () => catalogs!.loadKeys(classId!),
  });
}

export interface UseWithholdingOperationCentersParams {
  catalogs?: Pick<WithholdingsCatalogs, 'loadOperationCenters'>;
  enabled?: boolean;
}

/** Carga los centros de operación (solo necesarios para claves municipales). */
export function useWithholdingOperationCenters({
  catalogs,
  enabled = true,
}: UseWithholdingOperationCentersParams) {
  return useQuery<WithholdingOperationCenterOption[]>({
    queryKey: withholdingOperationCentersQueryKey(),
    enabled: enabled && Boolean(catalogs?.loadOperationCenters),
    staleTime: STALE_TIME,
    queryFn: () => catalogs!.loadOperationCenters!(),
  });
}
